import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Button, Chip, Select, TextField, ViewTab } from "@legends/ui";
import {
  createGlobalRollItem,
  deleteGlobalRollItem,
  fetchConfigurableCaves,
  fetchGlobalRollItems,
  setCaveRollContrib,
  setRollItemLocked,
  uploadGlobalRollItem,
  type Cave,
  type RollItem,
  type RollName,
} from "./api";
import { Section, alertStyle, errMsg, monoLabelStyle, rowStyle } from "./sections";

// Pre/Post/Hold-roll admin (ADR 0052/0055). The super-admin owns the GLOBAL
// looping playlists (a hype reel before, a thanks/sponsor reel after, and the
// rotating hold slate played when a broadcast pauses its video) plus a per-item
// lock (caves cannot remove a locked global) and the per-cave "can add" grant.
// The hold playlist (ADR 0055) replaced ADR 0053's single hold image.

const wellInputStyle: CSSProperties = {
  boxSizing: "border-box",
  background: "var(--well)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  color: "var(--text-hi)",
  fontFamily: "var(--font-sans)",
  fontSize: "var(--fs-body)",
  padding: "7px 10px",
  outline: "none",
};

const DEFAULT_DWELL = 8000;

export default function RollAdminScreen() {
  const [pre, setPre] = useState<RollItem[]>([]);
  const [post, setPost] = useState<RollItem[]>([]);
  const [hold, setHold] = useState<RollItem[]>([]);
  const [caves, setCaves] = useState<Cave[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchGlobalRollItems()
      .then((pl) => {
        setPre(pl.pre);
        setPost(pl.post);
        setHold(pl.hold ?? []);
      })
      .catch((err: unknown) => setError(errMsg(err)));
    fetchConfigurableCaves()
      .then(setCaves)
      .catch(() => setCaves([]));
  }, []);

  useEffect(() => {
    setError(null);
    load();
  }, [load]);

  return (
    <div style={{ maxWidth: 820, display: "grid", gap: 16 }}>
      <RollColumn roll="pre" title="Pre-roll (hype reel)" items={pre} onChange={load} onError={setError} />
      <RollColumn roll="post" title="Post-roll (thanks / sponsors)" items={post} onChange={load} onError={setError} />
      <RollColumn roll="hold" title="Hold (paused-video slate)" items={hold} onChange={load} onError={setError} />
      <CaveContribSection caves={caves} onError={setError} />
      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}
    </div>
  );
}

function RollColumn({
  roll,
  title,
  items,
  onChange,
  onError,
}: {
  roll: RollName;
  title: string;
  items: RollItem[];
  onChange: () => void;
  onError: (msg: string) => void;
}) {
  const [itemTitle, setItemTitle] = useState("");
  const [kind, setKind] = useState<"image" | "video">("image");
  const [url, setUrl] = useState("");
  const [dwell, setDwell] = useState(String(DEFAULT_DWELL));
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const guard = (p: Promise<unknown>) => {
    setBusy(true);
    p.then(onChange)
      .catch((err: unknown) => onError(errMsg(err)))
      .finally(() => setBusy(false));
  };

  const addByUrl = () => {
    if (!url.trim()) return;
    guard(
      createGlobalRollItem({
        roll,
        kind,
        url: url.trim(),
        title: itemTitle.trim(),
        position: items.length,
        durationMs: kind === "image" ? Number(dwell) || DEFAULT_DWELL : 0,
      }),
    );
    setItemTitle("");
    setUrl("");
  };

  const upload = (file: File) => {
    const field = file.type.startsWith("video") ? "video" : "image";
    guard(
      uploadGlobalRollItem(field, file, {
        roll,
        title: itemTitle.trim(),
        position: items.length,
        durationMs: field === "image" ? Number(dwell) || DEFAULT_DWELL : 0,
      }),
    );
    setItemTitle("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const toggleLock = (it: RollItem) => guard(setRollItemLocked(it.id, !it.locked));
  const remove = (it: RollItem) => guard(deleteGlobalRollItem(it.id));

  return (
    <Section title={title}>
      <div style={{ marginTop: 12 }}>
        {items.map((it) => (
          <div key={it.id} style={rowStyle}>
            <span style={{ fontSize: "var(--fs-body-2)", color: "var(--text-hi)" }}>
              {it.title || "(untitled)"}
            </span>
            <Chip color="var(--accent)">{it.kind.toUpperCase()}</Chip>
            <span style={monoLabelStyle}>
              {it.kind === "video" ? "FULL" : `${it.durationMs || DEFAULT_DWELL}MS`}
            </span>
            {it.locked && <Chip>LOCKED</Chip>}
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <Button
                size="sm"
                variant={it.locked ? "accent" : "muted"}
                aria-label={it.locked ? `Unlock ${it.title}` : `Lock ${it.title}`}
                onClick={() => toggleLock(it)}
              >
                {it.locked ? "Unlock" : "Lock"}
              </Button>
              <Button
                size="sm"
                variant="muted"
                aria-label={`Delete ${it.title}`}
                onClick={() => remove(it)}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
            No {roll}-roll items yet.
          </p>
        )}
      </div>

      <form
        aria-label={`Add ${roll}-roll item`}
        onSubmit={(e) => {
          e.preventDefault();
          addByUrl();
        }}
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: "1px solid var(--border-subtle)",
          display: "flex",
          alignItems: "flex-end",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <TextField label="Title" name={`${roll}-title`} value={itemTitle} onChange={setItemTitle} />
        <div style={{ display: "flex", gap: 2 }} role="group" aria-label="Kind">
          {(["image", "video"] as const).map((k) => (
            <ViewTab key={k} label={k.toUpperCase()} active={kind === k} onClick={() => setKind(k)} />
          ))}
        </div>
        <TextField label="Asset URL" name={`${roll}-url`} value={url} onChange={setUrl} />
        {kind === "image" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <label htmlFor={`${roll}-dwell`} style={monoLabelStyle}>
              Dwell (ms)
            </label>
            <input
              id={`${roll}-dwell`}
              type="number"
              min={500}
              aria-label="Dwell milliseconds"
              value={dwell}
              onChange={(e) => setDwell(e.target.value)}
              style={{ ...wellInputStyle, width: 100 }}
            />
          </div>
        )}
        <Button variant="accent" type="submit" disabled={busy || !url.trim()}>
          Add by URL
        </Button>
        <label style={{ ...monoLabelStyle, cursor: "pointer", color: "var(--accent)" }}>
          ＋ Upload file
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*"
            aria-label={`Upload ${roll}-roll file`}
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload(f);
            }}
          />
        </label>
      </form>
    </Section>
  );
}

function CaveContribSection({
  caves,
  onError,
}: {
  caves: Cave[];
  onError: (msg: string) => void;
}) {
  const [caveId, setCaveId] = useState<string>("");
  // Local override of the flag after a toggle, so the chip updates without a
  // full caves refetch.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!caveId && caves.length > 0) setCaveId(caves[0].id);
  }, [caves, caveId]);

  const cave = caves.find((c) => c.id === caveId);
  const allowed = cave ? (overrides[cave.id] ?? !!cave.allowRollContrib) : false;

  const toggle = () => {
    if (!cave) return;
    const next = !allowed;
    setCaveRollContrib(cave.id, next)
      .then(() => setOverrides((o) => ({ ...o, [cave.id]: next })))
      .catch((err: unknown) => onError(errMsg(err)));
  };

  return (
    <Section title="Cave contributions">
      <p style={{ margin: "8px 0 12px", fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
        Let a cave's operators add their own pre/post-roll items (they can also
        hide removable global items). Locked global items always play.
      </p>
      {caves.length === 0 ? (
        <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>No caves.</p>
      ) : (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <Select
            label="Cave"
            aria-label="Cave"
            value={caveId}
            onChange={setCaveId}
            options={caves.map((c) => ({ value: c.id, label: c.name }))}
          />
          <Chip color={allowed ? "var(--accent)" : undefined}>
            {allowed ? "CONTRIBUTIONS ON" : "CONTRIBUTIONS OFF"}
          </Chip>
          <Button
            variant={allowed ? "muted" : "accent"}
            aria-label={allowed ? "Disable roll contributions" : "Enable roll contributions"}
            onClick={toggle}
          >
            {allowed ? "Disable" : "Enable"}
          </Button>
        </div>
      )}
    </Section>
  );
}
